/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/* eslint-disable @typescript-eslint/no-explicit-any -- pcg-core is loose-typed by design */

// The timing engine's Evaluation Range chrome (upstream Medaica Viewer v0.9.1,
// `installMvp2V09Controls`), as JSX:
//
//   PcgEvaluationRangeStrip     — `#mvp2AnalysisStrip`, after the waveform:
//                                 "Evaluation Range · Evaluation range for
//                                 results · a–b s · N cycles" when the engine
//                                 selected a range, "Analysis unavailable ·
//                                 Timing engine did not run" when its block is
//                                 present but not ready.
//   PcgEvaluationRangeConfidence — `#mvp2ConfidenceRow`, above the Provisional
//                                 Results: Signal / Timing / Variation / Energy
//                                 for the range (ready only).
//
// Both render nothing for a guide without the engine block. Wording, classes
// and the browser tooltips are upstream's, except the unavailable strip's
// tooltip: upstream's tells the reader to restart the standalone viewer with
// its analysis runtime, which a reviewer here cannot do, so it says instead
// what the missing range means for the review. Upstream's element ids are not
// carried (the viewer can be mounted more than once).

import React, { ReactElement } from "react"
import {
  ENGINE_READY,
  ENGINE_UNAVAILABLE,
  evaluationRangeConfidence,
  evaluationRangeSummary,
} from "../core/evaluation-range"

const UNAVAILABLE_TITLE =
  "The timing engine could not produce an Evaluation Range for this recording, so no provisional results are " +
  "shown. Review the recording by listening and with the guides."

export const PcgEvaluationRangeStrip = ({ core }: { core: any }): ReactElement | null => {
  const engine = core.ctx.engine
  if (engine?.status === ENGINE_UNAVAILABLE) {
    return (
      <div className="mvp2-analysis-strip" data-testid="pcg-evaluation-range-strip" title={UNAVAILABLE_TITLE}>
        <div className="mvp2-analysis-label">Analysis unavailable</div>
        <div className="mvp2-analysis-actions">
          <span className="mvp2-analysis-meta">Timing engine did not run</span>
        </div>
      </div>
    )
  }
  if (engine?.status !== ENGINE_READY) return null
  return (
    <div
      className="mvp2-analysis-strip"
      data-testid="pcg-evaluation-range-strip"
      title="Results are calculated from this highlighted section. The rest of the waveform is shown for context."
    >
      <div className="mvp2-analysis-label">Evaluation Range</div>
      <div className="mvp2-analysis-actions">
        <span className="mvp2-analysis-meta">{evaluationRangeSummary(engine.selected)}</span>
      </div>
    </div>
  )
}

export const PcgEvaluationRangeConfidence = ({ core }: { core: any }): ReactElement | null => {
  const engine = core.ctx.engine
  if (engine?.status !== ENGINE_READY) return null
  // Energy reads the metric scope's Energy gate (upstream analysisQuality()).
  const levels = evaluationRangeConfidence(engine.selected, core.analysisQuality())
  const items: Array<[string, string, string]> = [
    ["signal", "Signal", levels.signal],
    ["timing", "Timing", levels.timing],
    ["variation", "Variation", levels.variation],
    ["energy", "Energy", levels.energy],
  ]
  return (
    <section
      className="mvp2-confidence-row"
      data-testid="pcg-evaluation-range-confidence"
      title="Eval Range confidence summary for review."
    >
      <div className="mvp2-confidence-title">Confidence</div>
      {items.map(([key, label, value]) => (
        <div key={key} className="mvp2-confidence-item">
          <span className="mvp2-confidence-label">{label}</span>
          <span className="mvp2-confidence-value" data-confidence={key}>
            {value}
          </span>
        </div>
      ))}
    </section>
  )
}
